import { useMemo, useRef, useState, type KeyboardEvent } from "react";
import { formatUsd, toInvoiceRow, toNumber, type InvoiceRow } from "@/utils/ezyvetModels";
import type { Invoice, InvoiceLineItem } from "@/types/ezyvet";

type SortKey = "code" | "description" | "quantity" | "unitPrice" | "total";
type Sort = { key: SortKey; dir: 1 | -1 } | null;

interface Props {
  lines: InvoiceLineItem[];
  invoice: Invoice | null;
  matchedLineId: string | null;
  /** The flagged item when it isn't on the invoice; shown as a ghost row. */
  missing?: { description: string; estimatedValue: number } | null;
}

const COLUMNS: { key: SortKey; label: string; numeric?: boolean }[] = [
  { key: "code", label: "Code" },
  { key: "description", label: "Item" },
  { key: "quantity", label: "Qty", numeric: true },
  { key: "unitPrice", label: "Unit", numeric: true },
  { key: "total", label: "Total", numeric: true },
];

function compare(a: InvoiceRow, b: InvoiceRow, key: SortKey): number {
  const x = a[key];
  const y = b[key];
  if (x === null || x === undefined) return 1;
  if (y === null || y === undefined) return -1;
  return typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y));
}

/**
 * Right pane: the consult's ezyVet invoice lines. Sortable columns; arrow keys
 * move between rows (Home/End jump); the line the flag matched is marked.
 */
export function InvoiceViewer({ lines, invoice, matchedLineId, missing }: Props) {
  const [sort, setSort] = useState<Sort>(null);
  const [active, setActive] = useState(0);
  const body = useRef<HTMLTableSectionElement>(null);

  const rows = useMemo(() => {
    const mapped = lines.map(toInvoiceRow);
    return sort ? [...mapped].sort((a, b) => compare(a, b, sort.key) * sort.dir) : mapped;
  }, [lines, sort]);

  const total = useMemo(() => {
    const fromInvoice = toNumber(invoice?.total);
    return fromInvoice ?? rows.reduce((sum, r) => sum + (r.total ?? 0), 0);
  }, [invoice, rows]);

  const toggleSort = (key: SortKey) =>
    setSort((s) => (s?.key !== key ? { key, dir: 1 } : s.dir === 1 ? { key, dir: -1 } : null));

  const focusRow = (i: number) => {
    const next = Math.max(0, Math.min(rows.length - 1, i));
    setActive(next);
    body.current?.querySelectorAll<HTMLTableRowElement>("tr[data-row]")[next]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTableSectionElement>) => {
    const keys: Record<string, number> = { ArrowDown: active + 1, ArrowUp: active - 1, Home: 0, End: rows.length - 1 };
    if (e.key in keys) {
      e.preventDefault();
      e.stopPropagation();
      focusRow(keys[e.key]);
    }
  };

  return (
    <section className="pane" aria-labelledby="invoice-title">
      <header className="pane-header">
        <h2 id="invoice-title">
          Invoice {invoice?.invoice_number ? <span className="mono muted">#{invoice.invoice_number}</span> : null}
        </h2>
        <span className="muted">{rows.length === 1 ? "1 line" : `${rows.length} lines`}</span>
      </header>

      <div className="pane-body pane-body--flush">
        {rows.length === 0 && !missing ? (
          <p className="muted" style={{ padding: 16 }}>
            No invoice lines on this consult yet.
          </p>
        ) : (
          <table className="table table--dense invoice-table">
            <caption className="sr-only">ezyVet invoice line items. Use arrow keys to move between rows.</caption>
            <thead>
              <tr>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    scope="col"
                    className={c.numeric ? "num" : undefined}
                    aria-sort={sort?.key === c.key ? (sort.dir === 1 ? "ascending" : "descending") : "none"}
                  >
                    <button type="button" className="th-sort" onClick={() => toggleSort(c.key)}>
                      {c.label}
                      <span aria-hidden="true">{sort?.key === c.key ? (sort.dir === 1 ? " ▲" : " ▼") : ""}</span>
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody ref={body} onKeyDown={onKeyDown}>
              {rows.map((r, i) => {
                const matched = matchedLineId !== null && r.id === matchedLineId;
                return (
                  <tr
                    key={r.id}
                    data-row
                    tabIndex={i === active ? 0 : -1}
                    onFocus={() => setActive(i)}
                    onClick={() => focusRow(i)}
                    className={`${matched ? "row-matched" : ""} ${i === active ? "row-active" : ""}`}
                    aria-label={matched ? `${r.description}, matched to the flag` : undefined}
                  >
                    <td className="mono">{r.code ?? "—"}</td>
                    <td>
                      {r.description}
                      {matched && <span className="badge yellow" style={{ marginLeft: 8 }}>matched</span>}
                      {r.discount ? <span className="muted"> · disc. {formatUsd(r.discount)}</span> : null}
                    </td>
                    <td className="num mono">{r.quantity ?? "—"}</td>
                    <td className="num mono">{formatUsd(r.unitPrice)}</td>
                    <td className="num mono">{formatUsd(r.total)}</td>
                  </tr>
                );
              })}
              {missing && (
                <tr className="row-missing" aria-label={`Not billed: ${missing.description}`}>
                  <td className="mono">—</td>
                  <td>
                    <span className="badge red" style={{ marginRight: 8 }}>
                      not billed
                    </span>
                    {missing.description}
                  </td>
                  <td className="num mono">—</td>
                  <td className="num mono">—</td>
                  <td className="num mono">~{formatUsd(missing.estimatedValue)}</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row" colSpan={4} className="num">
                  Invoice total
                </th>
                <td className="num mono">{formatUsd(total)}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </section>
  );
}
